const CONSENT_STORAGE_KEY = 'bp.cookie-consent.v1';

export const ANALYTICS_STORAGE_KEYS = {
  queue: 'mp.analytics.queue.v1',
  anonymousId: 'mp.analytics.anonymous-id',
  sessionId: 'mp.analytics.session-id'
} as const;

export const COOKIE_CONSENT_VERSION = '2026-05-06';
export const COOKIE_CONSENT_CHANGED_EVENT = 'bp-cookie-consent-changed';

export interface CookieConsentPreferences {
  version: string;
  analytics: boolean;
  decidedAt: string;
  source: 'banner' | 'preferences';
}

const isBrowser = typeof window !== 'undefined';

export const readCookieConsent = (): CookieConsentPreferences | null => {
  if (!isBrowser) {
    return null;
  }

  try {
    const raw = window.localStorage.getItem(CONSENT_STORAGE_KEY);
    if (!raw) {
      return null;
    }

    const parsed = JSON.parse(raw) as Partial<CookieConsentPreferences>;
    if (parsed.version !== COOKIE_CONSENT_VERSION || typeof parsed.analytics !== 'boolean') {
      return null;
    }

    return {
      version: COOKIE_CONSENT_VERSION,
      analytics: parsed.analytics,
      decidedAt: typeof parsed.decidedAt === 'string' ? parsed.decidedAt : new Date(0).toISOString(),
      source: parsed.source === 'preferences' ? 'preferences' : 'banner'
    };
  } catch {
    return null;
  }
};

export const hasAnalyticsConsent = (): boolean => readCookieConsent()?.analytics === true;

export const clearAnalyticsStorage = (): void => {
  if (!isBrowser) {
    return;
  }

  window.localStorage.removeItem(ANALYTICS_STORAGE_KEYS.queue);
  window.localStorage.removeItem(ANALYTICS_STORAGE_KEYS.anonymousId);
  window.sessionStorage.removeItem(ANALYTICS_STORAGE_KEYS.sessionId);
};

export const saveCookieConsent = (
  preferences: Pick<CookieConsentPreferences, 'analytics'>,
  source: CookieConsentPreferences['source'] = 'banner'
): CookieConsentPreferences => {
  const next: CookieConsentPreferences = {
    version: COOKIE_CONSENT_VERSION,
    analytics: preferences.analytics,
    decidedAt: new Date().toISOString(),
    source
  };

  if (!isBrowser) {
    return next;
  }

  if (!next.analytics) {
    clearAnalyticsStorage();
  }

  window.localStorage.setItem(CONSENT_STORAGE_KEY, JSON.stringify(next));
  window.dispatchEvent(new CustomEvent(COOKIE_CONSENT_CHANGED_EVENT, { detail: next }));
  return next;
};
